import type { FeedItemKey } from './index';

/** Ordered row input with a stable key and non-negative height in px. */
export interface LayoutRow {
  readonly key: FeedItemKey;
  readonly height: number;
}

/** Prefix-sum layout snapshot; prefix[i] is the px offset before rows[i]. */
export interface PrefixLayout {
  readonly rows: LayoutRow[];
  readonly prefix: number[];
  readonly contentH: number;
  readonly indexByKey: Map<FeedItemKey, number>;
}

/** Item-relative anchor whose alpha is clamped to [0, 1] and offset is px from viewport top. */
export interface ItemAnchor {
  readonly kind: 'item';
  readonly key: FeedItemKey;
  readonly alpha: number;
  readonly offsetPx: number;
}

/** Anchor that pins the viewport to the bottom of the computed canvas. */
export interface BottomAnchor {
  readonly kind: 'bottom';
}

/** Anchor variant used to preserve visual position across layout changes. */
export type LayoutAnchor = ItemAnchor | BottomAnchor;
/** Origin mode moves content; scroll mode writes scrollTop to satisfy the anchor. */
export type AnchorSolveMode = 'origin' | 'scroll';

/** Solved origin, scrollTop, and canvas height in px after anchoring. */
export interface SolvedGeometry {
  readonly origin: number;
  readonly scrollTop: number;
  readonly canvasH: number;
}

/** Runway bounds in px; callers must satisfy min <= target <= max. */
export interface RunwayConfig {
  readonly min: number;
  readonly target: number;
  readonly max: number;
}

/** Half-open row index range [start, end) selected for rendering. */
export interface RenderRange {
  readonly start: number;
  readonly end: number;
}

/** Last known scroll direction used to bias overscan row allocation. */
export type RenderDirection = 'down' | 'idle' | 'up';

/** Minimum real rows kept ahead of a fast fling before placeholders are allowed. */
export const LEADING_REAL_ROWS = 8;

/** Render plan with ordered row indices and keys that should mount as placeholders. */
export interface RenderWindowPlan {
  readonly itemIndices: readonly number[];
  readonly placeholderKeys: ReadonlySet<FeedItemKey>;
  readonly range: RenderRange;
}

/** Builds an immutable prefix layout and throws on duplicate keys or invalid heights. */
export function buildPrefixLayout(rows: LayoutRow[]): PrefixLayout {
  const prefix: number[] = new Array(rows.length + 1);
  const indexByKey = new Map<FeedItemKey, number>();
  let cursor = 0;
  prefix[0] = 0;

  rows.forEach((row, index) => {
    assertFiniteNonNegative(row.height, `height for ${row.key}`);
    if (indexByKey.has(row.key)) {
      throw new Error(`duplicate layout key: ${row.key}`);
    }
    indexByKey.set(row.key, index);
    cursor += row.height;
    prefix[index + 1] = cursor;
  });

  return { rows: [...rows], prefix, contentH: cursor, indexByKey };
}

/** Returns the row's top offset in px from the content origin, or throws for a missing key. */
export function offsetOf(layout: PrefixLayout, key: FeedItemKey): number {
  const index = indexOf(layout, key);
  return layout.prefix[index];
}

/** Returns the row height in px, or throws for a missing key. */
export function heightOf(layout: PrefixLayout, key: FeedItemKey): number {
  const index = indexOf(layout, key);
  return layout.rows[index].height;
}

/** Returns the row's absolute y position in px for the given content origin. */
export function yOf(layout: PrefixLayout, origin: number, key: FeedItemKey): number {
  return origin + offsetOf(layout, key);
}

/** Clamps scrollTop to the legal [0, canvasH - viewportH] px range. */
export function clampScrollTop(scrollTop: number, canvasH: number, viewportH: number): number {
  assertFiniteNonNegative(canvasH, 'canvasH');
  assertFiniteNonNegative(viewportH, 'viewportH');
  return clamp(scrollTop, 0, Math.max(0, canvasH - viewportH));
}

/** Computes the starting content origin in px for a bottom-pinned or runway-backed window. */
export function initialOriginForWindow(input: {
  contentH: number;
  viewportH: number;
  bottomZoneH: number;
  bottomInsetH?: number;
  hasOlder: boolean;
  runwayTarget: number;
  startCapH: number;
}): number {
  assertFiniteNonNegative(input.contentH, 'contentH');
  assertFiniteNonNegative(input.viewportH, 'viewportH');
  assertFiniteNonNegative(input.bottomZoneH, 'bottomZoneH');
  const bottomInsetH = input.bottomInsetH ?? 0;
  assertFiniteNonNegative(bottomInsetH, 'bottomInsetH');

  if (input.hasOlder) return input.runwayTarget;
  return Math.max(input.startCapH, input.viewportH - bottomInsetH - input.contentH - input.bottomZoneH);
}

/** Solves geometry that preserves the requested anchor; throws if an item anchor key is absent. */
export function solveAnchor(input: {
  layout: PrefixLayout;
  anchor: LayoutAnchor;
  mode?: AnchorSolveMode;
  origin: number;
  scrollTop: number;
  viewportH: number;
  bottomZoneH: number;
  bottomInsetH?: number;
}): SolvedGeometry {
  const { layout, anchor, viewportH, bottomZoneH } = input;
  const bottomInsetH = input.bottomInsetH ?? 0;
  assertFiniteNonNegative(bottomInsetH, 'bottomInsetH');
  let origin = input.origin;
  let scrollTop = input.scrollTop;

  if (anchor.kind === 'item') {
    const itemOffset = offsetOf(layout, anchor.key);
    const itemH = heightOf(layout, anchor.key);
    const alpha = clamp(anchor.alpha, 0, 1);
    if (input.mode === 'scroll') {
      scrollTop = origin + itemOffset + alpha * itemH - anchor.offsetPx;
    } else {
      origin = scrollTop + anchor.offsetPx - itemOffset - alpha * itemH;
    }
  }

  let canvasH = origin + layout.contentH + bottomZoneH + bottomInsetH;

  if (anchor.kind === 'bottom') {
    scrollTop = clampScrollTop(canvasH - viewportH, canvasH, viewportH);
  } else {
    scrollTop = clampScrollTop(scrollTop, canvasH, viewportH);
  }

  canvasH = origin + layout.contentH + bottomZoneH + bottomInsetH;
  return { origin, scrollTop, canvasH };
}

/** Returns the anchor's px offset from viewport top at the current geometry. */
export function anchorOffset(input: {
  layout: PrefixLayout;
  key: FeedItemKey;
  origin: number;
  scrollTop: number;
  alpha?: number;
}): number {
  const alpha = clamp(input.alpha ?? 0, 0, 1);
  return yOf(input.layout, input.origin, input.key) + alpha * heightOf(input.layout, input.key) - input.scrollTop;
}

/** Picks the first visible row as a stable top anchor, or undefined when no row intersects the viewport. */
export function pickTopVisibleAnchor(input: {
  layout: PrefixLayout;
  origin: number;
  scrollTop: number;
  viewportH: number;
}): ItemAnchor | undefined {
  const { layout, origin, scrollTop, viewportH } = input;
  const viewportBottom = scrollTop + viewportH;
  const index = lowerBoundBy(
    0,
    layout.rows.length,
    (candidate) => origin + layout.prefix[candidate] + layout.rows[candidate].height > scrollTop
  );
  if (index >= layout.rows.length) return undefined;

  const y = origin + layout.prefix[index];
  if (y >= viewportBottom) return undefined;
  return {
    kind: 'item',
    key: layout.rows[index].key,
    alpha: 0,
    offsetPx: y - scrollTop
  };
}

/** Picks the visible row nearest the readable viewport center, excluding bottom inset px. */
export function pickResizeAnchor(input: {
  layout: PrefixLayout;
  origin: number;
  scrollTop: number;
  viewportH: number;
  bottomInsetH?: number;
}): ItemAnchor | undefined {
  const { layout, origin, scrollTop, viewportH } = input;
  const bottomInsetH = input.bottomInsetH ?? 0;
  assertFiniteNonNegative(bottomInsetH, 'bottomInsetH');
  const viewportTop = scrollTop;
  const readableViewportH = Math.max(0, viewportH - bottomInsetH);
  const viewportBottom = scrollTop + readableViewportH;
  const viewportCenter = scrollTop + readableViewportH / 2;
  if (readableViewportH <= 0 || layout.rows.length === 0) return undefined;

  const visibleStart = lowerBoundBy(
    0,
    layout.rows.length,
    (index) => origin + layout.prefix[index] + layout.rows[index].height > viewportTop
  );
  const visibleEnd = lowerBoundBy(
    0,
    layout.rows.length,
    (index) => origin + layout.prefix[index] >= viewportBottom
  );
  if (visibleStart >= visibleEnd) return undefined;

  const centerAt = (index: number) => origin + layout.prefix[index] + layout.rows[index].height / 2;
  const insertion = lowerBoundBy(visibleStart, visibleEnd, (index) => centerAt(index) >= viewportCenter);
  const left = previousPositiveRow(layout, insertion - 1, visibleStart);
  const right = nextPositiveRow(layout, insertion, visibleEnd);
  let bestIndex = left;
  if (right !== undefined) {
    if (bestIndex === undefined || centerAt(right) - viewportCenter < viewportCenter - centerAt(bestIndex)) {
      bestIndex = right;
    }
  }
  if (bestIndex === undefined) return undefined;

  const row = layout.rows[bestIndex];
  const y = origin + layout.prefix[bestIndex];
  const alpha = clamp((viewportCenter - y) / row.height, 0, 1);
  return {
    kind: 'item',
    key: row.key,
    alpha,
    offsetPx: y + alpha * row.height - scrollTop
  };
}

/** Moves origin upward by the prepended block height in px to preserve visible rows. */
export function applyPrependOrigin(origin: number, prependedHeight: number): number {
  assertFiniteNonNegative(prependedHeight, 'prependedHeight');
  return origin - prependedHeight;
}

/** Returns the px shift needed to restore runway target, or 0 when origin is in bounds. */
export function planRunwayRebase(origin: number, runway: RunwayConfig): number {
  if (runway.min > runway.target || runway.target > runway.max) {
    throw new Error('runway must satisfy min <= target <= max');
  }
  if (origin < runway.min || origin > runway.max) {
    return runway.target - origin;
  }
  return 0;
}

/** Applies a runway shift in px to origin, scrollTop, and canvas height together. */
export function applyRebase(geometry: SolvedGeometry, shift: number): SolvedGeometry {
  return {
    origin: geometry.origin + shift,
    scrollTop: geometry.scrollTop + shift,
    canvasH: geometry.canvasH + shift
  };
}

/** Builds a render plan, preserving viewport rows and substituting placeholders when row budget is tight. */
export function renderWindowPlanForViewport(input: {
  direction?: RenderDirection;
  focusPinnedKey?: FeedItemKey | null;
  layout: PrefixLayout;
  leadingRealRows?: number;
  maxRows?: number;
  origin: number;
  overscanAfterPx?: number;
  overscanBeforePx?: number;
  scrollTop: number;
  viewportH: number;
  overscanPx: number;
}): RenderWindowPlan {
  const overscanBeforePx = input.overscanBeforePx ?? input.overscanPx;
  const overscanAfterPx = input.overscanAfterPx ?? input.overscanPx;
  const range = findRenderRange(
    input.layout,
    input.origin,
    input.scrollTop - overscanBeforePx,
    input.scrollTop + input.viewportH + overscanAfterPx
  );
  const maxRows = input.maxRows === undefined ? undefined : Math.max(0, Math.floor(input.maxRows));
  const focusPinnedIndex = focusPinnedIndexForLayout(input.layout, input.focusPinnedKey);
  if (maxRows === undefined) {
    return {
      itemIndices: orderedItemIndicesForWindow(range, indicesForRange(range, focusPinnedIndex), focusPinnedIndex),
      range,
      placeholderKeys: new Set()
    };
  }

  if (range.end - range.start <= maxRows) {
    const realIndices = indicesForRange(range);
    if (focusPinnedIndex !== undefined && !realIndices.has(focusPinnedIndex) && realIndices.size < maxRows) {
      realIndices.add(focusPinnedIndex);
    }
    return {
      itemIndices: orderedItemIndicesForWindow(range, realIndices, focusPinnedIndex),
      range,
      placeholderKeys: new Set()
    };
  }

  const viewportRange = findRenderRange(
    input.layout,
    input.origin,
    input.scrollTop,
    input.scrollTop + input.viewportH
  );
  const direction = input.direction ?? 'idle';
  const realIndices =
    direction === 'idle'
      ? allocateIdleRealIndices(range, viewportRange, maxRows, focusPinnedIndex)
      : allocateDirectionalRealIndices(
          range,
          viewportRange,
          maxRows,
          direction,
          input.leadingRealRows ?? LEADING_REAL_ROWS,
          focusPinnedIndex
        );
  const placeholderKeys = new Set<FeedItemKey>();
  const itemIndices = orderedItemIndicesForWindow(range, realIndices, focusPinnedIndex);
  for (const index of itemIndices) {
    if (!realIndices.has(index)) {
      placeholderKeys.add(input.layout.rows[index].key);
    }
  }

  return { itemIndices, range, placeholderKeys };
}

function findRenderRange(
  layout: PrefixLayout,
  origin: number,
  startY: number,
  endY: number
): RenderRange {
  const rowCount = layout.rows.length;
  if (rowCount === 0) return { start: 0, end: 0 };

  const start = lowerBoundBy(
    0,
    rowCount,
    (index) => origin + layout.prefix[index] + layout.rows[index].height >= startY
  );
  const matchedEnd = lowerBoundBy(
    0,
    rowCount,
    (index) => origin + layout.prefix[index] > endY
  );
  // Preserve the legacy sentinel behavior for a range wholly above content.
  const end = matchedEnd === 0 ? rowCount : matchedEnd;
  return { start: Math.min(start, end), end };
}

function previousPositiveRow(
  layout: PrefixLayout,
  fromIndex: number,
  minIndex: number
): number | undefined {
  if (fromIndex < minIndex) return undefined;
  if (layout.prefix[fromIndex + 1] > layout.prefix[fromIndex]) return fromIndex;

  const boundary = layout.prefix[fromIndex];
  const firstBoundary = lowerBound(layout.prefix, boundary, minIndex, fromIndex + 1);
  const candidate = firstBoundary - 1;
  return candidate >= minIndex ? candidate : undefined;
}

function nextPositiveRow(
  layout: PrefixLayout,
  fromIndex: number,
  endIndex: number
): number | undefined {
  if (fromIndex >= endIndex) return undefined;
  if (layout.prefix[fromIndex + 1] > layout.prefix[fromIndex]) return fromIndex;

  const boundary = layout.prefix[fromIndex];
  const candidate = upperBound(layout.prefix, boundary, fromIndex + 1, endIndex + 1) - 1;
  return candidate < endIndex ? candidate : undefined;
}

function lowerBound(
  values: readonly number[],
  target: number,
  start = 0,
  end = values.length
): number {
  return lowerBoundBy(start, end, (index) => values[index] >= target);
}

function upperBound(
  values: readonly number[],
  target: number,
  start = 0,
  end = values.length
): number {
  return lowerBoundBy(start, end, (index) => values[index] > target);
}

function lowerBoundBy(start: number, end: number, matches: (index: number) => boolean): number {
  let low = start;
  let high = end;
  while (low < high) {
    const middle = low + Math.floor((high - low) / 2);
    if (matches(middle)) {
      high = middle;
    } else {
      low = middle + 1;
    }
  }
  return low;
}

function allocateIdleRealIndices(
  range: RenderRange,
  viewportRange: RenderRange,
  maxRows: number,
  focusPinnedIndex: number | undefined
): ReadonlySet<number> {
  const realIndices = new Set<number>();
  addAscending(realIndices, viewportRange.start, viewportRange.end);

  const viewportCount = viewportRange.end - viewportRange.start;
  if (viewportCount >= maxRows) return realIndices;

  let remaining = maxRows - viewportCount;
  if (focusPinnedIndex !== undefined && !realIndices.has(focusPinnedIndex) && remaining > 0) {
    realIndices.add(focusPinnedIndex);
    remaining -= 1;
  }

  const beforeAvailable = viewportRange.start - range.start;
  const afterAvailable = range.end - viewportRange.end;
  const [preferredBefore, preferredAfter] = preferredExtraRows(
    beforeAvailable,
    afterAvailable,
    remaining,
    'idle'
  );
  remaining -= addAscendingWithLimit(
    realIndices,
    viewportRange.start - preferredBefore,
    viewportRange.start,
    preferredBefore
  );
  remaining -= addAscendingWithLimit(
    realIndices,
    viewportRange.end,
    viewportRange.end + preferredAfter,
    preferredAfter
  );

  if (remaining > 0) {
    remaining -= addAscendingWithLimit(realIndices, range.start, viewportRange.start, remaining);
  }
  if (remaining > 0) {
    addAscendingWithLimit(realIndices, viewportRange.end, range.end, remaining);
  }

  return realIndices;
}

function allocateDirectionalRealIndices(
  range: RenderRange,
  viewportRange: RenderRange,
  maxRows: number,
  direction: Exclude<RenderDirection, 'idle'>,
  leadingRealRows: number,
  focusPinnedIndex: number | undefined
): ReadonlySet<number> {
  const realIndices = new Set<number>();
  addAscending(realIndices, viewportRange.start, viewportRange.end);

  const viewportCount = viewportRange.end - viewportRange.start;
  if (viewportCount >= maxRows) return realIndices;

  let remaining = maxRows - viewportCount;
  const leadingTarget = Math.max(0, Math.floor(leadingRealRows));

  if (direction === 'down') {
    const leadingEnd = Math.min(range.end, viewportRange.end + Math.min(leadingTarget, remaining));
    addAscending(realIndices, viewportRange.end, leadingEnd);
    remaining -= leadingEnd - viewportRange.end;

    if (focusPinnedIndex !== undefined && !realIndices.has(focusPinnedIndex) && remaining > 0) {
      realIndices.add(focusPinnedIndex);
      remaining -= 1;
    }

    const trailingStart = Math.max(range.start, viewportRange.start - remaining);
    addAscending(realIndices, trailingStart, viewportRange.start);
    remaining -= viewportRange.start - trailingStart;

    const extraLeadingEnd = Math.min(range.end, leadingEnd + remaining);
    addAscending(realIndices, leadingEnd, extraLeadingEnd);
  } else {
    const leadingStart = Math.max(range.start, viewportRange.start - Math.min(leadingTarget, remaining));
    addAscending(realIndices, leadingStart, viewportRange.start);
    remaining -= viewportRange.start - leadingStart;

    if (focusPinnedIndex !== undefined && !realIndices.has(focusPinnedIndex) && remaining > 0) {
      realIndices.add(focusPinnedIndex);
      remaining -= 1;
    }

    const trailingEnd = Math.min(range.end, viewportRange.end + remaining);
    addAscending(realIndices, viewportRange.end, trailingEnd);
    remaining -= trailingEnd - viewportRange.end;

    const extraLeadingStart = Math.max(range.start, leadingStart - remaining);
    addAscending(realIndices, extraLeadingStart, leadingStart);
  }

  return realIndices;
}

function addAscending(indices: Set<number>, start: number, end: number) {
  for (let index = start; index < end; index += 1) {
    indices.add(index);
  }
}

function addAscendingWithLimit(indices: Set<number>, start: number, end: number, limit: number): number {
  let added = 0;
  for (let index = start; index < end && added < limit; index += 1) {
    if (indices.has(index)) continue;
    indices.add(index);
    added += 1;
  }
  return added;
}

function focusPinnedIndexForLayout(
  layout: PrefixLayout,
  focusPinnedKey: FeedItemKey | null | undefined
): number | undefined {
  return focusPinnedKey == null ? undefined : layout.indexByKey.get(focusPinnedKey);
}

function indicesForRange(range: RenderRange, extraIndex?: number): Set<number> {
  const indices = new Set<number>();
  addAscending(indices, range.start, range.end);
  if (extraIndex !== undefined) indices.add(extraIndex);
  return indices;
}

function orderedItemIndicesForWindow(
  range: RenderRange,
  realIndices: ReadonlySet<number>,
  focusPinnedIndex: number | undefined
): number[] {
  const itemIndices = indicesForRange(range);
  if (focusPinnedIndex !== undefined) {
    if (realIndices.has(focusPinnedIndex)) {
      itemIndices.add(focusPinnedIndex);
    } else {
      itemIndices.delete(focusPinnedIndex);
    }
  }
  return [...itemIndices].sort((left, right) => left - right);
}

function preferredExtraRows(
  beforeAvailable: number,
  afterAvailable: number,
  budget: number,
  direction: RenderDirection
): [number, number] {
  const leadingBudget = direction === 'idle' ? Math.floor(budget / 2) : Math.ceil(budget * 0.75);
  const beforeFirst = direction === 'up' || direction === 'idle';
  const firstAvailable = beforeFirst ? beforeAvailable : afterAvailable;
  const secondAvailable = beforeFirst ? afterAvailable : beforeAvailable;
  const first = Math.min(firstAvailable, leadingBudget);
  const second = Math.min(secondAvailable, budget - first);
  const leftover = budget - first - second;
  const firstWithLeftover = first + Math.min(firstAvailable - first, leftover);

  return beforeFirst
    ? [firstWithLeftover, second]
    : [second, firstWithLeftover];
}

function indexOf(layout: PrefixLayout, key: FeedItemKey): number {
  const index = layout.indexByKey.get(key);
  if (index === undefined) {
    throw new Error(`missing layout key: ${key}`);
  }
  return index;
}

function assertFiniteNonNegative(value: number, name: string) {
  if (!Number.isFinite(value) || value < 0) {
    throw new Error(`${name} must be a finite non-negative number`);
  }
}

/** Clamps a numeric value to the inclusive [min, max] range. */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
